export type OperationName = "generate" | "delete";
export type OperationStatus = "queued" | "running" | "waiting" | "succeeded" | "failed";

export interface ShowPage<T> {
  data: T[];
  pagination: { page: number; pageSize: number; total: number };
}

export interface ShowStory {
  id: string;
  name: string | null;
  engine: string;
  status: { steps: Record<string, { type: "boolean"; done: boolean } | { type: "number"; progress: number; total: number }> };
  createdAt: string;
  updatedAt: string;
}

export interface EngineInputField {
  name: string;
  label: string;
  control: "text" | "textarea" | "integer" | "select";
  required: boolean;
  description?: string;
  min?: number;
  max?: number;
  defaultValue?: string | number;
  options?: Array<{ value: string; label: string }>;
  visibleWhen?: { field: string; equals: string };
}

export interface EngineStep {
  id: string;
  description: string;
  operations: OperationName[];
  inputs: Partial<Record<OperationName, Record<string, string>>>;
  inputFields: Partial<Record<OperationName, EngineInputField[]>>;
}

export interface ShowEngine {
  id: string;
  steps: EngineStep[];
}

export interface ShowOperation {
  id: string;
  storyId: string;
  engine: string;
  step: string;
  name: OperationName;
  targetKey: string;
  commandScope: string;
  input: Record<string, unknown>;
  status: OperationStatus;
  stage: string | null;
  state: Record<string, unknown> | null;
  outputKeys: string[] | null;
  error: Record<string, unknown> | null;
  evidence?: Record<string, { type: "json" | "text"; data: string }> | null;
  price: number | null;
  workflowId: string | null;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
}

export interface ShowArtifact {
  id: string;
  storyId: string;
  key: string;
  type: ShowArtifactType;
  data: string;
  validity: "valid" | "stale";
  staleReasons: string[];
  signedUrl?: string;
}

export type ShowArtifactType = "json" | "text" | "image-url" | "video-url";

export type ShowArtifactTreeEntry =
  | { kind: "folder"; name: string; prefix: string }
  | {
      kind: "file";
      name: string;
      id: string;
      key: string;
      type: ShowArtifactType;
      validity: "valid" | "stale";
      staleReasons: string[];
    };

export interface ShowArtifactTreeResponse {
  parent: string | null;
  entries: ShowArtifactTreeEntry[];
}

export interface DeleteShowArtifactsResponse {
  deletedCount: number;
  deleted: Array<{ id: string; key: string; type: ShowArtifactType }>;
}

export const activeOperation = (status: OperationStatus): boolean => ["queued", "running", "waiting"].includes(status);

export function storyTitle(story: ShowStory): string {
  return story.name?.trim() || "Untitled story";
}
